import {
  column,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  ModalBody,
  ModalHeader,
  numericColumn,
  simpleHeader,
  Tag,
  Tooltip,
} from "@homebound/beam";
import { Price } from "src/components";
import { CheckoutPricingTracePage_OptionStatsFragment } from "./CheckoutPricingTracePage.generated";

type CheckoutOptionDiffModalProps = { entries: CheckoutPricingTracePage_OptionStatsFragment[] };

type Totals = {
  costInCents: number;
  selectionFeeInCents: number;
  minimumPriceInCents: number;
  storedPriceInCents?: number | null;
  priceInCents: number;
};

type OptionDiffRow =
  | { kind: "header" }
  | { kind: "total"; data: Totals }
  | { kind: "entry"; id: string; data: CheckoutPricingTracePage_OptionStatsFragment };

function changeType(e: CheckoutPricingTracePage_OptionStatsFragment): "Added" | "Removed" | "Changed" {
  if (!e.previousOption && e.option) return "Added";
  if (e.previousOption && !e.option) return "Removed";
  return "Changed";
}

/** Modal showing the option-level pricing changes between two configs. */
export function CheckoutOptionDiffModal(props: CheckoutOptionDiffModalProps) {
  const { entries } = props;

  const totals: Totals = {
    costInCents: entries.sum((e) => e.costInCents),
    selectionFeeInCents: entries.sum((e) => e.selectionFeeInCents),
    minimumPriceInCents: entries.sum((e) => e.minimumPriceInCents),
    storedPriceInCents: entries.some((e) => e.storedPriceInCents != null)
      ? entries.sum((e) => e.storedPriceInCents ?? 0)
      : undefined,
    priceInCents: entries.sum((e) => e.priceInCents),
  };

  const columns: GridColumn<OptionDiffRow>[] = [
    column<OptionDiffRow>({
      header: "Option Group",
      total: "Total",
      entry: (e) => {
        const type = changeType(e);
        const tagType = type === "Added" ? "success" : type === "Removed" ? "warning" : "info";
        const prev = e.previousOption;
        const next = e.option;
        const changed = prev && next && prev.name !== next.name;
        return (
          <div css={Css.df.fdc.gap1.$}>
            <div css={Css.df.gap1.aic.$}>
              <Tag text={type} type={tagType} />
              <Tooltip title={[e.group.id, e.group.group?.type?.name].compact().join(" ")}>
                {e.group.name}
                {e.group.readyPlanLocation?.marketingName && ` (${e.group.readyPlanLocation.marketingName})`}
              </Tooltip>
            </div>
            {changed ? (
              <div css={Css.sm.gray700.$}>
                <Tooltip title={`${prev.id} ${prev.code}`}>{prev.name}</Tooltip>
                {" → "}
                <Tooltip title={`${next.id} ${next.code}`}>{next.name}</Tooltip>
              </div>
            ) : next || prev ? (
              <div css={Css.sm.gray700.$}>
                <Tooltip title={`${(next ?? prev)!.id} ${(next ?? prev)!.code}`}>{(next ?? prev)!.name}</Tooltip>
              </div>
            ) : null}
          </div>
        );
      },
      w: "400px",
    }),
    numericColumn<OptionDiffRow>({
      header: "Cost",
      total: (d) => <Price valueInCents={d.costInCents} displayDirection />,
      entry: (e) => <Price valueInCents={e.costInCents} displayDirection />,
    }),
    numericColumn<OptionDiffRow>({
      header: () => <Tooltip title="Selection Fee / Minimum Price / Contracted Structure Price">Overrides</Tooltip>,
      total: (d) => <OverridesCell data={d} />,
      entry: (e) => <OverridesCell data={e} />,
    }),
    numericColumn<OptionDiffRow>({
      header: "Price",
      total: (d) => <Price valueInCents={d.priceInCents} displayDirection />,
      entry: (e) => <Price valueInCents={e.priceInCents} displayDirection />,
    }),
  ];

  const rows: GridDataRow<OptionDiffRow>[] = [
    simpleHeader,
    { kind: "total" as const, id: "total", data: totals },
    ...entries.map((e, i) => ({ kind: "entry" as const, id: `${e.group.id}-${i}`, data: e })),
  ];

  return (
    <>
      <ModalHeader>Option Diff</ModalHeader>
      <ModalBody>
        <GridTable columns={columns} rows={rows} />
      </ModalBody>
    </>
  );
}

/** Renders the override values stacked and right-aligned. */
function OverridesCell(props: {
  data: Pick<Totals, "selectionFeeInCents" | "minimumPriceInCents" | "storedPriceInCents">;
}) {
  const { data } = props;
  return (
    <div css={Css.df.fdc.aife.$}>
      <div>{renderOverrideDelta(data.selectionFeeInCents)}</div>
      <div>{renderOverrideDelta(data.minimumPriceInCents)}</div>
      <div>{renderStructurePrice(data.storedPriceInCents)}</div>
    </div>
  );
}

/** Renders a delta override, or a dash when no override is set. */
function renderOverrideDelta(valueInCents: number) {
  return valueInCents === 0 ? "-" : <Price valueInCents={valueInCents} displayDirection />;
}

/** Renders an absolute structure price override, or a dash when no override is set. */
function renderStructurePrice(storedPriceInCents: number | null | undefined) {
  return storedPriceInCents === null ? "-" : <Price valueInCents={storedPriceInCents} />;
}
